import { Actionable, ActionableType, AVAILABLE_SERVICE, CONSULTATION_TYPE, PostMessageType } from '@abiglobalhealth/go-types'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { healthNavigatorServiceSelected } from '@/api/navigator'
import { ConsultationIcon } from '@/components/Consultation/components/ConsultationIcon'
import { OpenLinkIcon } from '@/components/icons/OpenLinkIcon'
import useNavigator from '@/hooks/useNavigator'
import { usePostMessage } from '@/hooks/usePostMessage'
import { useUser } from '@/hooks/useUser'
import { getSlugTranslation } from '@/locale/utils'
import { isAbortError } from '@/utils/errors'
import { cn } from '@/utils/tailwind'

export const RecommendedActionButton = ({ action, recommended }: { action: Actionable; recommended?: boolean }) => {
  const { t } = useTranslation()
  const user = useUser()
  const { sendPostMessage } = usePostMessage()
  const { navigator } = useNavigator()
  const abortControllerRef = useRef<AbortController | null>(null)

  const handleClick = async () => {
    let url: URL | null = null
    const conversationId = navigator.healthNavigator.conversationId

    try {
      switch (action.type) {
        case ActionableType.ABI_SERVICE:
          {
            abortControllerRef.current = new AbortController()

            await healthNavigatorServiceSelected(
              {
                uniqueId: user?.user?.data.uniqueId!,
                serviceType: action.name as AVAILABLE_SERVICE
              },
              abortControllerRef.current.signal
            )
            const searchParams = new URLSearchParams()
            searchParams.set('q', action.name)
            if (conversationId) searchParams.set('cid', conversationId)
            url = new URL(window.location.href)
            url.search = searchParams.toString()
            abortControllerRef.current = null
          }

          break

        case ActionableType.CLIENT_SERVICE:
          if (action.referenceUrl) url = new URL(action.referenceUrl)
          break

        default:
          throw new Error('Unsupported action type')
      }

      await sendPostMessage(PostMessageType.NAVIGATOR_RECOMMENDED_ACTION_CLICK, {
        ...action,
        conversationId: navigator.healthNavigator.conversationId!
      })
      if (url) window.location.href = url.toString()
    } catch (error: unknown) {
      if (!isAbortError(error)) throw error
    }
  }

  const isAbiService = action.type === ActionableType.ABI_SERVICE
  const consultationType = action.name as CONSULTATION_TYPE

  return (
    <button
      data-testid="recommended-action-button"
      className={cn('border-primary relative flex w-full items-center justify-center rounded-full px-6 py-4', recommended ? 'border-2' : 'border')}
      onClick={handleClick}
    >
      {recommended && (
        <span className="text-primary absolute top-0 left-1/2 -mt-[.9rem] -translate-x-1/2 rounded-full bg-white px-3 py-1 text-xs">
          {t('health_navigator.recommended')}
        </span>
      )}

      {isAbiService ? (
        <ConsultationIcon
          consultationType={consultationType}
          customClassName="text-primary absolute left-6 top-1/2 -translate-y-1/2 h-6 xs:block hidden"
        />
      ) : (
        <OpenLinkIcon className="text-primary xs:block absolute top-1/2 left-6 hidden h-6 -translate-y-1/2" />
      )}

      <p className="text-primary font-bold uppercase">
        {isAbiService ? getSlugTranslation(`general.consultation.${consultationType}.title`) : action.label}
      </p>
    </button>
  )
}
